/**
 * Vocabulário de finalidade comercial no frontend.
 *
 * `transaction_type` tem três valores — `venda`, `aluguel` e `venda_aluguel` —
 * e é uma projeção de `pricing_infos`, que é a fonte da verdade. Comparar com
 * `=== 'venda'` trata o campo como binário e faz o imóvel misto ser rotulado
 * como aluguel; por isso as telas devem usar estes helpers.
 */

export type BusinessType = 'SALE' | 'RENTAL';

const SALE_VALUES = ['venda', 'venda_aluguel', 'sale', 'buy'];
const RENTAL_VALUES = ['aluguel', 'venda_aluguel', 'rent', 'rental'];

export const includesSale = (transactionType?: string | null): boolean =>
  SALE_VALUES.includes(String(transactionType ?? ''));

export const includesRental = (transactionType?: string | null): boolean =>
  RENTAL_VALUES.includes(String(transactionType ?? ''));

export const isMixed = (transactionType?: string | null): boolean =>
  includesSale(transactionType) && includesRental(transactionType);

/** Rótulo curto para badge/tabela. */
export const transactionLabel = (transactionType?: string | null): string => {
  if (isMixed(transactionType)) return 'Venda e Locação';
  if (includesRental(transactionType)) return 'Aluguel';
  return 'Venda';
};

/** Rótulo em minúsculas para frase corrida ("disponível para ..."). */
export const transactionPhrase = (transactionType?: string | null): string => {
  if (isMixed(transactionType)) return 'venda e locação';
  if (includesRental(transactionType)) return 'locação';
  return 'venda';
};

export const transactionEmoji = (transactionType?: string | null): string => {
  if (isMixed(transactionType)) return '🔁';
  return includesRental(transactionType) ? '🏠' : '💰';
};

export const transactionColor = (transactionType?: string | null): string => {
  if (isMixed(transactionType)) return '#0f766e';
  return includesRental(transactionType) ? '#8b5cf6' : '#3b82f6';
};

/** Modalidades derivadas do campo legado, para telas sem `pricing_infos`. */
export const businessTypesFor = (transactionType?: string | null): BusinessType[] => {
  const result: BusinessType[] = [];
  if (includesSale(transactionType)) result.push('SALE');
  if (includesRental(transactionType)) result.push('RENTAL');
  return result.length ? result : ['SALE'];
};
